import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FaCheckCircle, FaCheck, FaTimes, FaFileDownload, FaFileAlt } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import fileDownload from 'js-file-download';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './App.css';

const saveTasksToLocalStorage = (tasks) => {
  localStorage.setItem('tasks', JSON.stringify(tasks));
};

const loadTasksFromLocalStorage = () => {
  const tasks = localStorage.getItem('tasks');
  return tasks ? JSON.parse(tasks) : { do: [], schedule: [], delegate: [], delete: [] };
};

const saveAuditTrailToLocalStorage = (auditTrail) => {
  localStorage.setItem('auditTrail', JSON.stringify(auditTrail));
};

const loadAuditTrailFromLocalStorage = () => {
  const auditTrail = localStorage.getItem('auditTrail');
  return auditTrail ? JSON.parse(auditTrail) : [];
};

const App = () => {
    const [tasks, setTasks] = useState(loadTasksFromLocalStorage() || { do: [], schedule: [], delegate: [], delete: [] });
    const [auditTrail, setAuditTrail] = useState(loadAuditTrailFromLocalStorage());
  const [showModal, setShowModal] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', dueDate: new Date().toISOString().split('T')[0], description: '', priority: '' });
  const [currentQuadrant, setCurrentQuadrant] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showCompleteConfirmModal, setShowCompleteConfirmModal] = useState(false);
  const [showIncompleteConfirmModal, setShowIncompleteConfirmModal] = useState(false);
  const [delayCloseSidebar, setDelayCloseSidebar] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('none');
  
  const { width, height } = useWindowSize();
  const sidebarRef = useRef(null);
  const modalRef = useRef(null);

  // Add handleClickOutside function to manage sidebar and modal interactions
useEffect(() => {
    const handleClickOutside = (event) => {
        if (
            sidebarRef.current &&
            !sidebarRef.current.contains(event.target) &&
            !document.querySelector('.modal.show')?.contains(event.target)
        ) {
            if (!delayCloseSidebar && !showCompleteConfirmModal && !showIncompleteConfirmModal) {
                setSelectedTask(null);
            }
        }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
}, [sidebarRef, delayCloseSidebar, showCompleteConfirmModal, showIncompleteConfirmModal]);

const addTask = () => {
    const timestamp = new Date().toISOString();
    
    if (isEditing) {
      const updatedTasks = tasks[currentQuadrant].map((task) =>
        task.id === editTaskId ? { ...task, ...newTask } : task
      );
      const auditEntry = { action: 'edit', task: { ...newTask, id: editTaskId }, timestamp };
      
      setTasks((prevTasks) => ({
        ...prevTasks,
        [currentQuadrant]: updatedTasks,
      }));
      
      setAuditTrail((prevAuditTrail) => [...prevAuditTrail, auditEntry]);
      
      saveTasksToLocalStorage({ ...tasks, [currentQuadrant]: updatedTasks });
      saveAuditTrailToLocalStorage([...auditTrail, auditEntry]);
      
      setSelectedTask({ ...selectedTask, ...newTask });
    } else {
      const task = {
        id: `task-${Date.now()}`,
        name: newTask.name,
        dueDate: newTask.dueDate,
        description: newTask.description,
        priority: newTask.priority,
        completed: false,
      };
      const auditEntry = { action: 'add', task, timestamp };
      
      setTasks((prevTasks) => ({
        ...prevTasks,
        [currentQuadrant]: [...prevTasks[currentQuadrant], task],
      }));
      
      setAuditTrail((prevAuditTrail) => [...prevAuditTrail, auditEntry]);
      
      saveTasksToLocalStorage({
        ...tasks,
        [currentQuadrant]: [...tasks[currentQuadrant], task],
      });
      saveAuditTrailToLocalStorage([...auditTrail, auditEntry]);
    }
  
    setShowModal(false);
    setNewTask({ name: '', dueDate: new Date().toISOString().split('T')[0], description: '', priority: '' });
    setIsEditing(false);
    setEditTaskId(null);
  };
  

  const handleShowModal = (quadrant) => {
    setCurrentQuadrant(quadrant);
    setShowModal(true);
  };

// Handle Task Completion and Incomplete Confirmation
const handleTaskCompletion = (id, quadrant) => {
    const quadrantTasks = tasks[quadrant] || [];
    console.log(`Handling completion for task ID ${id} in quadrant ${quadrant}`, quadrantTasks);
  
    const task = quadrantTasks.find((task) => task.id === id);
  
    if (!task) {
      console.error(`Task with ID ${id} not found in quadrant ${quadrant}`);
      return;
    }
  
    setSelectedTask(task);
  
    if (task.completed) {
      setShowIncompleteConfirmModal(true);
    } else {
      setShowCompleteConfirmModal(true);
    }
  
    setCurrentQuadrant(quadrant); // Ensure currentQuadrant is set
    setDelayCloseSidebar(true); // Delay sidebar close
};

  

const confirmTaskCompletion = (id, quadrant) => {
    const quadrantTasks = tasks[quadrant] || [];
    console.log(`Confirming completion for task ID ${id} in quadrant ${quadrant}`, quadrantTasks);
  
    const updatedTasks = quadrantTasks.map((task) =>
      task.id === id ? { ...task, completed: !task.completed } : task
    );
  
    const completedTask = updatedTasks.find((task) => task.id === id);
  
    if (!completedTask) {
      console.error(`Task with ID ${id} not found after update in quadrant ${quadrant}`);
      return;
    }
  
    const auditEntry = {
      action: completedTask.completed ? 'mark as complete' : 'mark as not complete',
      task: { id: completedTask.id, name: completedTask.name, dueDate: completedTask.dueDate, description: completedTask.description, priority: completedTask.priority, completed: completedTask.completed },
      timestamp: new Date().toISOString(),
    };
  
    setTasks((prevTasks) => ({
      ...prevTasks,
      [quadrant]: updatedTasks,
    }));
  
    setAuditTrail((prevAuditTrail) => [...prevAuditTrail, auditEntry]);
    saveTasksToLocalStorage({
      ...tasks,
      [quadrant]: updatedTasks,
    });
    saveAuditTrailToLocalStorage([...auditTrail, auditEntry]);
  
    console.log(`Task with ID ${id} found and updated in quadrant ${quadrant}`);
  
    if (completedTask.completed) {
      setShowCompletionModal(true);
      setShowConfetti(true); // Show confetti
    } else {
      setSelectedTask(null); // Close the sidebar after marking as not complete
      setDelayCloseSidebar(false); // Allow sidebar to close
    }
};


  const deleteTask = (id, quadrant) => {
    const updatedTasks = tasks[quadrant].filter((task) => task.id !== id);
    const auditEntry = { action: 'delete', task: { id }, timestamp: new Date().toISOString() };
    setTasks((prevTasks) => ({
      ...prevTasks,
      [quadrant]: updatedTasks,
    }));
    setAuditTrail((prevAuditTrail) => [...prevAuditTrail, auditEntry]);
    saveTasksToLocalStorage({
      ...tasks,
      [quadrant]: updatedTasks,
    });
    saveAuditTrailToLocalStorage([...auditTrail, auditEntry]);
    setSelectedTask(null); // Close the sidebar after deleting the task
  };

  const editTask = (task, quadrant) => {
    setNewTask(task);
    setCurrentQuadrant(quadrant);
    setIsEditing(true);
    setEditTaskId(task.id);
    setShowModal(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const sourceQuadrant = source.droppableId;
    const destQuadrant = destination.droppableId;

    if (sourceQuadrant === destQuadrant) {
      const items = Array.from(tasks[sourceQuadrant]);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      setTasks((prevTasks) => ({
        ...prevTasks,
        [sourceQuadrant]: items,
      }));
      saveTasksToLocalStorage({
        ...tasks,
        [sourceQuadrant]: items,
      });
    } else {
      const sourceItems = Array.from(tasks[sourceQuadrant]);
      const [movedItem] = sourceItems.splice(source.index, 1);
      const destItems = Array.from(tasks[destQuadrant]);
      destItems.splice(destination.index, 0, movedItem);
      setTasks((prevTasks) => ({
        ...prevTasks,
        [sourceQuadrant]: sourceItems,
        [destQuadrant]: destItems,
      }));
      saveTasksToLocalStorage({
        ...tasks,
        [sourceQuadrant]: sourceItems,
        [destQuadrant]: destItems,
      });
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const getTaskCounts = (quadrant) => {
    const totalTasks = tasks[quadrant].length;
    const completedTasks = tasks[quadrant].filter((task) => task.completed).length;
    return `${completedTasks}/${totalTasks}`;
  };

  const applyFilterAndSort = (tasks) => {
    let filteredTasks = tasks;
    
    if (filter !== 'all') {
      filteredTasks = tasks.filter((task) => {
        if (filter === 'completed') {
          return task.completed;
        } else if (filter === 'not_started') {
          return !task.completed;
        }
        return true;
      });
    }
  
    if (sort !== 'none') {
      filteredTasks = [...filteredTasks].sort((a, b) => {
        if (sort === 'due_date') {
          return new Date(a.dueDate) - new Date(b.dueDate);
        } else if (sort === 'priority') {
          const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return 0;
      });
    }
  
    return filteredTasks;
  };

  // Function to convert tasks to Excel
  const tasksToExcel = (tasks) => {
    const headers = ['Quadrant', 'ID', 'Name', 'Due Date', 'Description', 'Priority', 'Completed'];
    const rows = [];
  
    Object.keys(tasks).forEach((quadrant) => {
      tasks[quadrant].forEach((task) => {
        rows.push({
          Quadrant: quadrant,
          ID: task.id,
          Name: task.name,
          'Due Date': task.dueDate,
          Description: task.description,
          Priority: task.priority,
          Completed: task.completed ? 'Yes' : 'No'
        });
      });
    });
  
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tasks');
    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  };

// Function to download tasks in Excel
const downloadTasks = () => {
    const excelData = tasksToExcel(tasks);
    const blob = new Blob([excelData], { type: 'application/octet-stream' });
    fileDownload(blob, 'EMtasks.xlsx');
};
  
// Function to convert audit trail to Excel
const auditTrailToExcel = (auditTrail) => {
    const headers = ['Action', 'Task', 'Date'];
    const rows = auditTrail.map((entry) => ({
        Action: entry.action,
        ID: entry.task.id,
        Name: entry.task.name,
        'Due Date': entry.task.dueDate,
        Description: entry.task.description,
        Priority: entry.task.priority,
        Completed: entry.task.completed ? 'Yes' : 'No',
        Timestamp: entry.timestamp,
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Trail');
    return XLSX.write(workbook, { type: 'array' });
  };
  
  // Function to download audit trail as Excel
  const viewAuditTrail = () => {
    const excelData = auditTrailToExcel(auditTrail);
    fileDownload(excelData, 'EMaudit-trail.xlsx');
  };
  
  

  return (
    <div className="app-wrapper">
      <div className="app">
        {showConfetti && <Confetti width={width} height={height} />}
        <h1 className="header">The Eisenhower Matrix</h1>
        <div className="filters">
          <Form.Control as="select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Tasks {filter === 'all' ? <FaCheck /> : <FaTimes />}</option>
            <option value="completed">Completed Tasks {filter === 'completed' ? <FaCheck /> : <FaTimes />}</option>
            <option value="not_started">Not Started Tasks {filter === 'not_started' ? <FaCheck /> : <FaTimes />}</option>
          </Form.Control>
          <Form.Control as="select" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="none">No Sorting {sort === 'none' ? <FaCheck /> : <FaTimes />}</option>
            <option value="due_date">Sort by Due Date {sort === 'due_date' ? <FaCheck /> : <FaTimes />}</option>
            <option value="priority">Sort by Priority {sort === 'priority' ? <FaCheck /> : <FaTimes />}</option>
          </Form.Control>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
  <div className="matrix">
    {['do', 'schedule', 'delegate', 'delete'].map((quadrant) => (
      <Droppable key={quadrant} droppableId={quadrant}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`quadrant ${quadrant}`}
          >
            <h2>
              {quadrant.charAt(0).toUpperCase() + quadrant.slice(1)} ({getTaskCounts(quadrant)})
            </h2>
            <p className="description">
              {quadrant === 'do' && 'Tasks with deadlines or consequences.'}
              {quadrant === 'schedule' && 'Tasks with unclear deadlines that contribute to long-term success.'}
              {quadrant === 'delegate' && 'Tasks that must get done but don’t require your specific skill set.'}
              {quadrant === 'delete' && 'Distractions and unnecessary tasks.'}
            </p>
            <ul className="task-list">
              {applyFilterAndSort(tasks[quadrant]).map((task, index) => (
                <Draggable key={task.id} draggableId={task.id} index={index}>
                  {(provided) => (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`draggable-block ${task.completed ? 'completed' : ''}`}
                    >
                      <div className="task-header" style={{ cursor: 'pointer' }}>
                        {task.completed ? (
                          <FaCheckCircle style={{ color: 'green', marginRight: '10px' }} />
                        ) : (
                          <input
                            type="radio"
                            checked={task.completed}
                            onChange={() => handleTaskCompletion(task.id, quadrant)}
                            style={{ marginRight: '10px' }}
                          />
                        )}
                        <span onClick={() => handleTaskClick(task)}>
                          {task.name} <span className={`badge badge-${task.priority.toLowerCase()}`}>{task.priority}</span>
                        </span>
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
            <Button variant="primary" onClick={() => handleShowModal(quadrant)}>
              Add Task
            </Button>
          </div>
        )}
      </Droppable>
    ))}
  </div>
</DragDropContext>


        {selectedTask && (
          <div className="sidebar" ref={sidebarRef}>
            <div className="sidebar-content">
              <h3>Task Details</h3>
              <p><strong>Name:</strong> {selectedTask.name}</p>
              <p><strong>Due Date:</strong> {selectedTask.dueDate}</p>
              <p><strong>Priority:</strong> <span className={`badge badge-${selectedTask.priority.toLowerCase()}`}>{selectedTask.priority}</span></p>
              <p>
                <strong>Status:</strong> 
                {selectedTask.completed ? (
                  <span className="badge badge-completed">
                    Completed <FaCheckCircle style={{ color: 'green' }} />
                  </span>
                ) : (
                  <span className="badge badge-not-started">
                    Not Started <FaCheckCircle style={{ color: 'lightgrey' }} />
                  </span>
                )}
              </p>
              <p><strong>Description:</strong> <span dangerouslySetInnerHTML={{ __html: selectedTask.description }} /></p>
            </div>
            <div className="sidebar-actions">
              <Button variant="primary" onClick={() => editTask(selectedTask, currentQuadrant)}>Edit</Button>
              <Button
  variant="success"
  onClick={() => {
    handleTaskCompletion(selectedTask.id, currentQuadrant);
  }}
>
  {selectedTask.completed ? 'Not Complete' : 'Mark as Complete'}
</Button>

              <Button variant="danger" onClick={() => setShowDeleteModal(true)}>Delete</Button>
            </div>
          </div>
        )}

<div className="vertical-buttons">
  <OverlayTrigger
    placement="left"
    overlay={<Tooltip id="tooltip-download">Download Tasks</Tooltip>}
  >
    <button className="icon-button" onClick={downloadTasks}>
      <FaFileDownload size={24} />
    </button>
  </OverlayTrigger>
  <OverlayTrigger
    placement="left"
    overlay={<Tooltip id="tooltip-audit">View Audit Trail</Tooltip>}
  >
    <button className="icon-button" onClick={viewAuditTrail}>
      <FaFileAlt size={24} />
    </button>
  </OverlayTrigger>
</div>


        <div ref={modalRef}>
          <Modal centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Delete</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to delete this task?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  deleteTask(selectedTask.id, currentQuadrant);
                  setShowDeleteModal(false);
                }}
              >
                Delete
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal centered show={showCompleteConfirmModal} onHide={() => setShowCompleteConfirmModal(false)}>
  <Modal.Header closeButton>
    <Modal.Title>Confirm Complete</Modal.Title>
  </Modal.Header>
  <Modal.Body>Are you sure you want to mark this task as complete?</Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowCompleteConfirmModal(false)}>
      Cancel
    </Button>
    <Button
      variant="success"
      onClick={() => {
        confirmTaskCompletion(selectedTask.id, currentQuadrant);
        setShowCompleteConfirmModal(false);
      }}
    >
      Mark as Complete
    </Button>
  </Modal.Footer>
</Modal>


          <Modal centered show={showIncompleteConfirmModal} onHide={() => setShowIncompleteConfirmModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Incomplete</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to mark this task as not complete?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowIncompleteConfirmModal(false)}>
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  confirmTaskCompletion(selectedTask.id, currentQuadrant);
                  setShowIncompleteConfirmModal(false);
                }}
              >
                Mark as Not Complete
              </Button>
            </Modal.Footer>
          </Modal>
          <Modal centered show={showCompletionModal} onHide={() => {
  setShowCompletionModal(false); 
  setDelayCloseSidebar(false); // Allow sidebar to close
  setSelectedTask(null); // Close the sidebar after completing the task
  setShowConfetti(false); // Hide confetti
}}>
  <Modal.Header closeButton>
    <Modal.Title className="text-center w-100">
      {selectedTask ? `${selectedTask.name} has been completed!` : 'Task Completed!'}
    </Modal.Title>
  </Modal.Header>
  <Modal.Body className="text-center">
    <FaCheckCircle size={60} color="green" />
  </Modal.Body>
  <Modal.Footer className="justify-content-center">
    <Button variant="primary" onClick={() => { 
      setShowCompletionModal(false); 
      setDelayCloseSidebar(false); // Allow sidebar to close
      setSelectedTask(null); // Close the sidebar after completing the task
      setShowConfetti(false); // Hide confetti
    }}>
      Close
    </Button>
  </Modal.Footer>
</Modal>

          <Modal show={showModal} onHide={() => setShowModal(false)} className="custom-modal">
            <Modal.Header closeButton>
              <Modal.Title>{isEditing ? 'Edit Task' : 'New Task'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                <Form.Group controlId="formTaskName">
                  <Form.Label>Task Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter task name"
                    value={newTask.name}
                    onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                  />
                </Form.Group>
                <Form.Group controlId="formDueDate">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                  />
                </Form.Group>
                <Form.Group controlId="formTaskPriority">
                  <Form.Label>Priority</Form.Label>
                  <Form.Control
                    as="select"
                    value={newTask.priority || ''}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                  >
                    <option value="">Select Priority</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group controlId="formDescription">
                  <Form.Label>Description</Form.Label>
                  <ReactQuill
                    value={newTask.description}
                    onChange={(value) => setNewTask({ ...newTask, description: value })}
                    placeholder="We support markdown! Try **bold**, `inline code`, or ``` for code blocks."
                    style={{ height: '150px' }} // Set height to twice the current height
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Close
              </Button>
              <Button variant="primary" onClick={addTask}>
                Save Changes
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default App;
